// Reporting's Download (owner, 2026-11-03: "a download button for exporting the
// pdf in view ... a pop-up modal could offer pdf vs Excel formats, as well as an
// option to include pdfs of the underlying invoices").
//
// WHAT IT PRODUCES (EXCEL ONLY since D-472, owner, 2026-11-04: "let's skip
// the PDF offering and just assume Excel"): one workbook, two sheets — the
// Table view's rows as numbers, and the Invoices view's rows. A workbook
// cannot carry PDF pages, so with the invoices included a SECOND file
// downloads beside it: the invoice report (`reportPdf.ts`: its cover listing
// and each invoice's own pages).
//
// Nothing is stored. The invoice report is still requested through
// `POST /reports`, so including the documents writes the same audit row the
// old Submit did; the chart and listing alone read nothing that is not already
// on screen.
import type { BarRowData } from '../components/DashboardCharts'
import type { Invoice } from './types'
import type { ReportChart } from './reportCharts'
import { downloadUrl } from './download'
import { formatDate, formatTimestampDate } from './format'
import { statusLabel } from './constants'
import { buildWorkbook, type Cell } from './xlsxWrite'

/** What a file needs of a report: its title and its two column headings. Every
    section's catalogue entry has them (`reportCharts.ts`, `overTimeCharts.ts`). */
type ExportableChart = Pick<ReportChart, 'title' | 'labelHeader' | 'valueHeader'>

interface ChartExportInput {
  chart: ExportableChart
  rows: readonly BarRowData[]
  /** True when the server left rows out of the read behind the chart. */
  truncated: boolean
  /** The filters, as sentences — the same lines the invoice report's cover prints. */
  criteriaLines: readonly string[]
  invoices: readonly Invoice[]
  /** The listing's full count, when it is larger than `invoices`. */
  invoiceTotal: number | null
  requestedBy: string
  at: Date
}

/** `2026-11-03 14.05` in Eastern time — a colon is illegal in a file name. */
function stamp(at: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}.${get('minute')}`
}

export function exportFileName(chart: ExportableChart, at: Date): string {
  return `${chart.title} ${stamp(at)}.xlsx`
}

const runOn = (at: Date) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(at)

/** Save bytes to the user's disk under `fileName`. */
export function saveBytes(bytes: Uint8Array, fileName: string, type: string): void {
  const url = URL.createObjectURL(new Blob([bytes as unknown as BlobPart], { type }))
  try {
    downloadUrl(url, fileName)
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }
}

// ---- Excel ---------------------------------------------------------------------

const LISTING_HEADER = ['Company', 'Vendor', 'Invoice #', 'Invoice Date', 'Uploaded Date', 'Exported Date', 'Status', 'Total']

function listingCells(inv: Invoice): Cell[] {
  return [
    inv.company_name ?? '',
    inv.vendor_name ?? '',
    inv.external_invoice_id ?? '',
    inv.invoice_date ? formatDate(inv.invoice_date) : '',
    formatTimestampDate(inv.uploaded_at),
    formatTimestampDate(inv.exported_at),
    statusLabel(inv.status),
    inv.total === null ? null : { money: inv.total },
  ]
}

/** Spend charts' values are dollars; every other chart's are counts, days or percents. */
function isMoneyChart(chart: ExportableChart): boolean {
  return chart.valueHeader === 'Spend'
}

export function buildChartWorkbook(input: ChartExportInput): Uint8Array {
  const preamble = [
    input.chart.title,
    `Run by ${input.requestedBy} on ${runOn(input.at)} (Eastern)`,
    ...(input.criteriaLines.length > 0 ? input.criteriaLines : ['Filters: none']),
    ...(input.truncated ? ['Some rows with small numbers on every measure are not shown.'] : []),
  ]
  const money = isMoneyChart(input.chart)
  return buildWorkbook([
    {
      name: input.chart.title,
      preamble,
      header: [input.chart.labelHeader, input.chart.valueHeader],
      rows: input.rows.map((row) => [row.label, money ? { money: row.total } : row.total]),
      widths: [48, 22],
    },
    {
      name: 'Invoices',
      preamble: [
        'Invoices Included in This View',
        ...(input.invoiceTotal !== null && input.invoiceTotal > input.invoices.length
          ? [`The first ${input.invoices.length} of ${input.invoiceTotal} invoices.`]
          : []),
      ],
      header: LISTING_HEADER,
      rows: input.invoices.map(listingCells),
      widths: [28, 32, 16, 14, 14, 14, 24, 14],
    },
  ])
}
